/* bolero-ad-attribution.js — ECOM-87: last-paid-click ad attribution.
   Stores last paid ad click (utm_medium=paid-ish, or gclid; gbraid/wbraid/fbclid-only
   are weak) in localStorage. Syncs onto the cart as ad_* attributes (merge-by-key, so
   e.g. Trybe's trybe_* are untouched) for shopify_d2c_sync to read off the order.
   Loaded `defer` right after an inline script stamping
   window.__boleroAdAttr.{cartClickAt,itemCount}.

   Round 2 (opus review 2026-09-25): M2 landing/page-load only POST with itemCount>0
   -- a bounce/bot never creates a cart; first add-to-cart syncs via cart:change. S1
   cart:change compares event.detail.cart directly, no sessionStorage short-circuit
   -- a same-tab new/cleared cart has a different token and must still re-sync. S3
   consent gated via Shopify.loadFeatures('consent-tracking-api'), ~2s
   proceed-anyway fallback. S5 clears a >=30d-stale cart ad_click_at once when no
   valid touch is stored. S6 gbraid/wbraid-only landings are weak, like fbclid. */
(function () {
  if (window.__boleroAdAttrReady) return;
  window.__boleroAdAttrReady = true;

  var STORAGE_KEY = 'bolero_ad_touch';
  var SYNCED_KEY = 'bolero_ad_synced';
  var MAX_LEN = 255;
  var EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; /* 30 days */
  var MAX_POSTS_PER_LOAD = 3;
  var CONSENT_LOAD_TIMEOUT_MS = 2000;

  /* utm_medium values (lowercased) counted as paid. gclid presence is paid
     regardless of utm_medium; gbraid/wbraid alone are weak (S6), same as fbclid. */
  var PAID_MEDIUMS = {
    paid: 1, cpc: 1, ppc: 1, cpm: 1, cpv: 1, demandgen: 1, display: 1,
    paid_social: 1, paidsocial: 1, 'paid-social': 1, social_paid: 1, 'social-paid': 1
  };

  /* [paramKey, adKey] — covers 7 of the 9 stored ad_* keys; ad_landing and
     ad_click_at are derived separately. gbraid/wbraid are classify()-only signals,
     never stored or sent. */
  var KEY_MAP = [
    ['utm_source', 'ad_utm_source'], ['utm_medium', 'ad_utm_medium'],
    ['utm_campaign', 'ad_utm_campaign'], ['utm_content', 'ad_utm_content'],
    ['utm_term', 'ad_utm_term'], ['fbclid', 'ad_fbclid'], ['gclid', 'ad_gclid']
  ];

  var inFlight = false;
  var postCount = 0;

  function truncate(v) {
    if (v == null) return '';
    var s = String(v).replace(/^\s+|\s+$/g, '');
    return s.length > MAX_LEN ? s.slice(0, MAX_LEN) : s;
  }

  function consentBlocksTracking() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.currentVisitorConsent === 'function') {
        var consent = cp.currentVisitorConsent();
        if (consent && typeof consent === 'object' && consent.marketing === 'no') return true;
      }
    } catch (e) {}
    return false;
  }

  /* S3: loadFeatures may not exist, may error, or may never call back -- any of
     those proceeds (same as '' / no decision yet). */
  function withConsentGate(cb) {
    var proceeded = false;
    var timer = null;

    function proceed() {
      if (proceeded) return;
      proceeded = true;
      if (timer) { try { clearTimeout(timer); } catch (e) {} }
      try { if (!consentBlocksTracking()) cb(); } catch (e) {}
    }

    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        timer = setTimeout(proceed, CONSENT_LOAD_TIMEOUT_MS);
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
          proceed();
        });
      } else {
        proceed();
      }
    } catch (e) {
      proceed();
    }
  }

  function getParams() {
    var out = {};
    var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'gbraid', 'wbraid'];
    var i, v;
    try {
      var qs = new URLSearchParams(window.location.search || '');
      for (i = 0; i < keys.length; i++) {
        v = qs.get(keys[i]);
        out[keys[i]] = v == null ? '' : truncate(v);
      }
    } catch (e) {
      for (i = 0; i < keys.length; i++) out[keys[i]] = '';
    }
    return out;
  }

  /* 'paid' | 'weak' | null (not a touch — change nothing) */
  function classify(params) {
    var medium = (params.utm_medium || '').toLowerCase();
    if (PAID_MEDIUMS.hasOwnProperty(medium) || params.gclid) return 'paid';
    if (params.fbclid || params.gbraid || params.wbraid) return 'weak';
    return null;
  }

  function isExpired(clickAtIso) {
    var t = Date.parse(clickAtIso);
    return isNaN(t) || (Date.now() - t) > EXPIRY_MS;
  }

  /* stored touch, or null if absent/unparseable/expired (expired = removed) */
  function readStoredTouch() {
    var raw, touch;
    try { raw = window.localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { touch = JSON.parse(raw); } catch (e) { return null; }
    if (!touch || typeof touch !== 'object' || !touch.ad_click_at) return null;
    if (isExpired(touch.ad_click_at)) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      return null;
    }
    return touch;
  }

  function writeStoredTouch(touch) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(touch)); } catch (e) {}
  }

  function buildTouch(params, kind) {
    var t = {
      ad_landing: truncate(window.location.pathname || ''),
      ad_click_at: new Date().toISOString(),
      kind: kind
    };
    for (var i = 0; i < KEY_MAP.length; i++) t[KEY_MAP[i][1]] = params[KEY_MAP[i][0]] || '';
    return t;
  }

  /* Applies the overwrite rules; returns the newly stored touch, or null. */
  function maybeStoreNewTouch() {
    var params = getParams();
    var kind = classify(params);
    if (!kind) return null;

    var shouldStore;
    if (kind === 'paid') {
      shouldStore = true; /* TAGGED-PAID always replaces */
    } else {
      var existing = readStoredTouch();
      /* WEAK replaces only if nothing stored (incl. expired, already null here) or stored is weak */
      shouldStore = !existing || existing.kind === 'weak';
    }
    if (!shouldStore) return null;

    var touch = buildTouch(params, kind);
    writeStoredTouch(touch);
    return touch;
  }

  /* touch may be {} (S5's clearing POST) -- every key then comes out ''. */
  function cartAttributesFor(touch) {
    var a = { ad_landing: touch.ad_landing || '', ad_click_at: touch.ad_click_at || '' };
    for (var i = 0; i < KEY_MAP.length; i++) a[KEY_MAP[i][1]] = touch[KEY_MAP[i][1]] || '';
    return a;
  }

  function rootPrefix() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (!root) root = '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  /* S1: keyed on click time + cart token, set only from the POST response's own
     confirmed attributes. Never read back to gate a sync -- observability only. */
  function rememberSynced(clickAt, token) {
    try { window.sessionStorage.setItem(SYNCED_KEY, clickAt + '|' + (token || '')); } catch (e) {}
  }

  /* POST attrs onto the cart; only remembers (S1) if the response confirms
     attributes.ad_click_at === expectClickAt. Sends only the 9 ad_* keys --
     attributes merge by key server-side, so nothing else on the cart is touched. */
  function syncCart(attrs, expectClickAt) {
    if (!attrs || inFlight || postCount >= MAX_POSTS_PER_LOAD) return;
    var body;
    try { body = JSON.stringify({ attributes: attrs }); } catch (e) { return; }

    inFlight = true;
    postCount += 1;
    function done() { inFlight = false; }

    try {
      fetch(rootPrefix() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body
      }).then(function (resp) {
        if (!resp || !resp.ok) { done(); return; }
        return resp.json().then(function (data) {
          done();
          try {
            var confirmed = data && data.attributes && data.attributes.ad_click_at;
            if (confirmed === expectClickAt) rememberSynced(expectClickAt, data.token);
          } catch (e) {}
        }, function () { done(); });
      }).catch(function (e) {
        done();
        try { console.debug('bolero-ad-attribution: sync failed', e); } catch (e2) {}
      });
    } catch (e) {
      done();
    }
  }

  function liquidCartClickAt() {
    try {
      var v = window.__boleroAdAttr && window.__boleroAdAttr.cartClickAt;
      return v ? String(v) : '';
    } catch (e) { return ''; }
  }

  /* M2: itemCount rendered by layout/theme.liquid from cart.item_count. */
  function liquidItemCount() {
    try {
      var n = Number(window.__boleroAdAttr && window.__boleroAdAttr.itemCount);
      return isNaN(n) ? 0 : n;
    } catch (e) { return 0; }
  }

  /* Prestige dispatches cart:change on document.documentElement (bubbles to
     document). Direct compare only (S1) -- this is the first-add-to-cart sync path
     (M2), must fire even if a marker from an earlier different-cart sync matches. */
  function onCartChange(event) {
    try {
      var touch = readStoredTouch();
      if (!touch) return;
      var cart = event && event.detail && event.detail.cart;
      if (!cart) return;
      var cartAttrs = cart.attributes || {};
      var cartClickAt = cartAttrs.ad_click_at || '';
      if (cartClickAt === touch.ad_click_at) return;
      syncCart(cartAttributesFor(touch), touch.ad_click_at);
    } catch (e) {}
  }

  function runMain() {
    var newTouch;
    try { newTouch = maybeStoreNewTouch(); } catch (e) { newTouch = null; }
    var itemCount = liquidItemCount();

    if (newTouch) {
      /* M2: an empty cart is left alone -- the first add-to-cart's cart:change
         syncs this same touch (readStoredTouch() picks it back up). */
      if (itemCount > 0) syncCart(cartAttributesFor(newTouch), newTouch.ad_click_at);
    } else {
      var touch = readStoredTouch();
      var cartClickAt = liquidCartClickAt();
      if (touch) {
        if (cartClickAt !== touch.ad_click_at && itemCount > 0) {
          syncCart(cartAttributesFor(touch), touch.ad_click_at); /* trigger (b) */
        }
      } else if (cartClickAt && itemCount > 0 && isExpired(cartClickAt)) {
        /* S5: no valid stored touch, but the cart still carries a stale (>=30d)
           ad_click_at nothing ever cleared. Clear it once. */
        syncCart(cartAttributesFor({}), '');
      }
    }

    document.addEventListener('cart:change', onCartChange); /* trigger (c) */
  }

  try { withConsentGate(runMain); } catch (e) {}
})();
